import { del, get, post, put } from '@/services/http'
import { endpoints } from '@/services/endpoints'

/**
 * Asset classes — the account's library of reusable types.
 *
 * A class is a template, not a thing on the plant floor: a machine model, a
 * line configuration, a site blueprint, a sensor type. Plant, Line and Machine
 * each carry a nullable `asset_class_id`, so the hierarchy holds the instances
 * and this holds what they are instances of.
 *
 * This is a LIVE endpoint. Everything here is a real write against a real
 * database, which is why the shape is pinned to the server's contract in one
 * place rather than assumed at four call sites.
 */

/**
 * The wire enum, in the API's own order.
 *
 * These four strings travel over the network and are validated server-side
 * against a Postgres enum. They are never translated, never reordered and never
 * extended from here — a fifth value has to exist in the database first.
 */
export const ENTITY_TYPES = ['plant', 'line', 'machine', 'sensor']

/** Longest name the `name` column accepts. Mirrored on the form so 150 characters fails in the browser rather than at the server. */
export const NAME_MAX_LENGTH = 150

/**
 * Wire → app.
 *
 * The API is snake_case and this codebase is camelCase, so the boundary is
 * here and nothing above it needs to know either fact. `id` is a UUID string
 * and stays opaque — it is never parsed, formatted or displayed.
 *
 * @param {{id: string, name: string, entity_type: string}} row
 */
const toAssetClass = (row) => ({
  id: row.id,
  name: row.name,
  entityType: row.entity_type,
  /* Null is account-wide; a plant id scopes the class to one site. Carried
     even though no control sets it yet, so the list can say which classes are
     site-specific rather than presenting every one as universal. */
  plantId: row.plant_id ?? null,
  createdAt: row.created_at ?? null,
})

/**
 * The whole library, in one read.
 *
 * ── Why it is not paged ──────────────────────────────────────────────────
 * The type rail counts how many classes exist of each kind, and a count off a
 * page is not a count. Paging would mean either a wrong number beside each type
 * or a request per type to fix it, and this is a library of templates — a few
 * dozen on a large account, not thousands.
 *
 * So it asks for the ceiling. `page_size` defaults to 50 server-side, which is
 * NOT enough: the screen read the first fifty and reported the type counts as if
 * that were all of them, which is worse than a short list because a confident
 * wrong number is hard to notice. 200 is the API's maximum.
 *
 * `total` comes back with it, so the one case this still cannot serve announces
 * itself instead of lying — see `truncated`.
 */
export const PAGE_CEILING = 200

/**
 * @returns {Promise<{items: Array<object>, total: number, truncated: boolean}>}
 */
export async function fetchAssetClasses(signal) {
  const data = await get(endpoints.assetClasses.list, {
    params: { page: 1, page_size: PAGE_CEILING, sort_by: 'name', sort_dir: 'asc' },
    signal,
  })

  /* `asset_classes`, not `items`. This read `data?.items` and the API has never
     served that key — so the list was empty whatever the account held, every
     type count was zero, and the asset-class dropdowns on Sites & Hierarchy and
     both sensor forms were empty with it. */
  const rows = data?.asset_classes ?? []
  const total = data?.total ?? rows.length

  return {
    items: rows.map(toAssetClass),
    total,
    /* True only past the ceiling, where the counts genuinely cannot be trusted.
       The page says so rather than quietly under-reporting. */
    truncated: total > rows.length,
  }
}

/**
 * @param {{name: string, entityType: string, plantId?: string|null}} input
 */
export async function createAssetClass({ name, entityType, plantId = null }) {
  const row = await post(endpoints.assetClasses.list, {
    name: name.trim(),
    entity_type: entityType,
    /* Required, and nullable. `AssetClassCreate` gives it no default, so the KEY
       must be present even when the value is null — omitting it is a 422, which
       is what every create on this screen used to be. */
    plant_id: plantId,
  })
  return toAssetClass(row)
}

/**
 * Rename only. The server's update schema accepts `name` and nothing else, so
 * `entity_type` is fixed at creation — a class cannot change what tier of thing
 * it describes, and the form says so rather than offering a control that would
 * be silently discarded.
 *
 * @param {{id: string, name: string}} input
 */
export async function renameAssetClass({ id, name }) {
  const row = await put(endpoints.assetClasses.detail(id), { name: name.trim() })
  return toAssetClass(row)
}

/**
 * Soft delete. The row survives with `deleted_at` set and drops out of every
 * listing, so anything already pointing at this class keeps a reference that
 * still resolves — removing a class does not reach into the hierarchy and
 * unassign it.
 */
export async function deleteAssetClass(id) {
  await del(endpoints.assetClasses.detail(id))
  return { id, deleted: true }
}
